import React from 'react'

export interface VisualInteractionUtils {
  highlightElement: (ref: React.RefObject<HTMLElement>, duration?: number) => Promise<void>
  typeIntoInput: (ref: React.RefObject<HTMLInputElement>, value: string, setter: (val: string) => void) => Promise<void>
  clickButton: (ref: React.RefObject<HTMLButtonElement>) => Promise<void>
  clickElement: (ref: React.RefObject<HTMLElement>) => Promise<void>
  selectDropdown: (ref: React.RefObject<HTMLSelectElement>, value: string, setter: (val: string) => void) => Promise<void>
  selectSymbol: (symbolButtonRef: React.RefObject<HTMLButtonElement>, symbol: string, onSymbolChange: (symbol: string) => void) => Promise<void>
  scrollIntoView: (ref: React.RefObject<HTMLElement>) => Promise<void>
  typeIntoSearchInput: (value: string) => Promise<boolean>
}

export const createVisualInteractionUtils = (): VisualInteractionUtils => {
  const highlightElement = async (ref: React.RefObject<HTMLElement>, duration = 1000): Promise<void> => {
    const element = ref.current
    if (!element) return
    
    const originalStyle = element.style.cssText
    
    // Add highlight animation
    element.style.cssText += `
      box-shadow: 0 0 20px 5px rgba(102, 126, 234, 0.8) !important;
      border: 2px solid #667eea !important;
      transition: all 0.3s ease !important;
      transform: scale(1.02) !important;
      z-index: 999 !important;
    `
    
    await new Promise(resolve => setTimeout(resolve, duration))
    
    // Restore original style
    element.style.cssText = originalStyle
  }

  const typeIntoInput = async (
    ref: React.RefObject<HTMLInputElement>, 
    value: string, 
    setter: (val: string) => void
  ): Promise<void> => {
    const element = ref.current
    if (!element) return
    
    await highlightElement(ref, 500)
    
    element.focus()
    setter('')
    
    // Type character by character
    for (let i = 0; i <= value.length; i++) {
      const currentValue = value.substring(0, i)
      setter(currentValue)
      await new Promise(resolve => setTimeout(resolve, 150))
    }
    
    element.blur()
  }

  const clickButton = async (ref: React.RefObject<HTMLButtonElement>): Promise<void> => {
    const element = ref.current
    if (!element) return
    
    await highlightElement(ref, 600)
    
    // Click animation
    element.style.transform = 'scale(0.95)'
    element.style.transition = 'transform 0.1s ease'
    
    setTimeout(() => {
      element.style.transform = ''
      element.click()
    }, 100)
  }

  const clickElement = async (ref: React.RefObject<HTMLElement>): Promise<void> => {
    const element = ref.current
    if (!element) return
    
    await highlightElement(ref, 600)
    
    // Click animation
    element.style.transform = 'scale(0.95)'
    element.style.transition = 'transform 0.1s ease'
    
    setTimeout(() => {
      element.style.transform = ''
      
      // Trigger both native and React synthetic events
      const clickEvent = new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window
      })
      element.dispatchEvent(clickEvent)
      
      // Also call native click for good measure
      element.click()
    }, 100)
  }

  const selectDropdown = async (
    ref: React.RefObject<HTMLSelectElement>, 
    value: string, 
    setter: (val: string) => void
  ): Promise<void> => {
    const element = ref.current
    if (!element) return
    
    await highlightElement(ref, 500)
    
    element.focus()
    setter(value)
    
    // Simulate change event
    const event = new Event('change', { bubbles: true })
    element.dispatchEvent(event)
    
    await new Promise(resolve => setTimeout(resolve, 300))
    element.blur()
  }

  const selectSymbol = async (
    symbolButtonRef: React.RefObject<HTMLButtonElement>, 
    symbol: string, 
    onSymbolChange: (symbol: string) => void
  ): Promise<void> => {
    const element = symbolButtonRef.current
    if (!element) return
    
    await highlightElement(symbolButtonRef, 800)
    
    // Click the symbol button to open dropdown/modal
    element.click()
    
    // Wait for modal/dropdown to open
    await new Promise(resolve => setTimeout(resolve, 300))
    
    // Try to find the symbol in the dropdown/modal
    // Look for symbol text in various possible containers
    const symbolSelectors = [
      `[data-symbol="${symbol}"]`,
      `button:contains("${symbol}")`,
      `div:contains("${symbol}")`,
      `.symbol-item:contains("${symbol}")`,
      `[value="${symbol}"]`
    ]
    
    let symbolElement = null
    
    // Try each selector
    for (const selector of symbolSelectors) {
      if (selector.includes(':contains(')) {
        // Handle :contains() pseudo-selector
        const baseSelector = selector.split(':contains(')[0]
        const searchText = symbol
        const elements = document.querySelectorAll(baseSelector || '*')
        
        for (const el of elements) {
          if (el.textContent?.includes(searchText)) {
            symbolElement = el as HTMLElement
            break
          }
        }
      } else {
        symbolElement = document.querySelector(selector) as HTMLElement
      }
      
      if (symbolElement) break
    }
    
    if (symbolElement) {
      await highlightElement({ current: symbolElement }, 600)
      symbolElement.click()
      
      // Update the symbol state
      onSymbolChange(symbol)
      
      // Wait for modal to close
      await new Promise(resolve => setTimeout(resolve, 500))
    } else {
      // Fallback: close the modal and set symbol directly
      const closeButton = document.querySelector('[aria-label="Close"], .close, .modal-close') as HTMLElement
      if (closeButton) {
        closeButton.click()
      }
      
      // Set symbol directly
      onSymbolChange(symbol)
      await new Promise(resolve => setTimeout(resolve, 300))
    }
  }

  const findSearchInput = (): HTMLInputElement | null => {
    // Try multiple strategies to find the search input
    const selectors = [
      'input[placeholder*="Search"]',
      'input[placeholder*="search"]', 
      'input[type="text"]',
      '[class*="searchInput"]',
      '[class*="search-input"]',
      '[class*="Search"]'
    ]
    
    for (const selector of selectors) {
      const element = document.querySelector(selector) as HTMLInputElement
      if (element && element.type === 'text') {
        return element
      }
    }
    
    return null
  }

  const typeIntoSearchInput = async (value: string): Promise<boolean> => {
    const searchInput = findSearchInput()
    if (!searchInput) {
      console.log('❌ Could not find search input')
      return false
    }
    
    console.log('✅ Found search input:', searchInput)
    
    // Highlight the input
    const originalStyle = searchInput.style.cssText
    searchInput.style.cssText += `
      box-shadow: 0 0 20px 5px rgba(102, 126, 234, 0.8) !important;
      border: 2px solid #667eea !important;
      transition: all 0.3s ease !important;
    `
    
    await new Promise(resolve => setTimeout(resolve, 500))
    
    // Focus and clear
    searchInput.focus()
    searchInput.value = ''
    
    // Type character by character
    for (let i = 0; i <= value.length; i++) {
      const currentValue = value.substring(0, i)
      searchInput.value = currentValue
      
      // Trigger input events
      searchInput.dispatchEvent(new Event('input', { bubbles: true }))
      searchInput.dispatchEvent(new Event('change', { bubbles: true }))
      
      await new Promise(resolve => setTimeout(resolve, 150))
    }
    
    // Restore original style
    searchInput.style.cssText = originalStyle
    
    return true
  }

  const scrollIntoView = async (ref: React.RefObject<HTMLElement>): Promise<void> => {
    const element = ref.current
    if (!element) return
    
    element.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'center',
      inline: 'nearest'
    })
    
    // Wait for scroll animation to complete
    await new Promise(resolve => setTimeout(resolve, 800))
  }

  return {
    highlightElement,
    typeIntoInput,
    clickButton,
    clickElement,
    selectDropdown,
    selectSymbol,
    scrollIntoView,
    typeIntoSearchInput
  }
}

export interface AnimationStatusProps {
  isAnimating: boolean
  lastAction: string
}

export const AnimationStatus: React.FC<AnimationStatusProps> = ({ isAnimating, lastAction }) => {
  if (!isAnimating) return null

  return (
    <div style={{
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      padding: '12px',
      borderRadius: '8px',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      position: 'relative',
      zIndex: 1000
    }}>
      <div style={{
        width: '20px',
        height: '20px',
        border: '2px solid white',
        borderTop: '2px solid transparent',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }}></div>
      <span>{lastAction}</span>
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
} 